import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Box,
  Button,
  FilterChipGroup,
  Link,
  Spinner,
  Text,
} from '@razorpay/blade/components';

import api from 'api';
import {
  CustomFieldFilterApplyResult,
  CustomFieldFilterProps,
  CustomFieldSelectedFilter,
  FilterableCustomFieldDefinition,
} from './types';
import {
  asFilterableDefinition,
  buildCustomFieldClauses,
  createFilterId,
  extractPeopleIdsFromUserIds,
  getFieldSection,
  getFilterableDefinitions,
  getInitialFiltersFromSavedView,
  getInitialFilterValue,
  hasFilterValue,
  isDateDefinition,
  isSelectableDefinition,
  isSupportedFilterDefinition,
} from './utils';
import {
  CUSTOM_FIELD_FILTER_QUERY_KEY,
  FIELDS_BY_USAGE_PAGE_SIZE,
  MAX_FILTERED_EMPLOYEES,
} from './constants';
import { DateFilterChip } from './DateFilterChip';
import { TextFilterChip } from './TextFilterChip';
import { EnumFilterChip } from './EnumFilterChip';
import { AddFilterMenu } from './AddFilterMenu';

export const CustomFieldFilter = ({
  viewType = 'PAYROLL_FILTER',
  disabled = false,
  canManageView = false,
  onApply,
  onClear,
  onError,
  onLoadingChange,
  onSelectedFiltersCountChange,
}: CustomFieldFilterProps) => {
  const [selectedFilters, setSelectedFilters] = useState<CustomFieldSelectedFilter[]>([]);
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const [openTextFilterKey, setOpenTextFilterKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRestoredSavedView = useRef(false);
  const updateSavedView = useMutation(api.employeeDataSettings.mutations.updateSavedCustomFieldView);

  // The saved view is an optional enhancement: some roles (for example payroll
  // operators without MANAGE_CUSTOM_FIELDS) get PermissionDenied from
  // GetSavedView. That failure must never block the filter panel — we simply
  // skip restoring/persisting the saved view and fall back to the usage list.
  const savedViewQuery = useQuery(
    [CUSTOM_FIELD_FILTER_QUERY_KEY, 'savedView', viewType],
    () => api.employeeDataSettings.queries.getSavedCustomFieldView({ viewType }),
  );

  const usageFieldsQuery = useQuery(
    [CUSTOM_FIELD_FILTER_QUERY_KEY, 'fieldsByUsage', viewType],
    () =>
      api.employeeDataSettings.queries.listFieldsByUsage({
        usages: ['PAYROLL_FILTER'],
        pageSize: FIELDS_BY_USAGE_PAGE_SIZE,
      }),
    {
      enabled: !savedViewQuery.isLoading,
    },
  );

  const definitions = useMemo(() => {
    const savedDefinitions =
      savedViewQuery.data?.payrollSavedCustomFieldView?.fieldDefinitions
        ?.map(asFilterableDefinition)
        .filter((definition): definition is FilterableCustomFieldDefinition => Boolean(definition)) ??
      [];

    if (savedDefinitions.length > 0) return getFilterableDefinitions(savedDefinitions);

    const usageDefinitions =
      usageFieldsQuery.data?.payrollFieldsByUsage?.definitions
        ?.map(asFilterableDefinition)
        .filter((definition): definition is FilterableCustomFieldDefinition => Boolean(definition)) ??
      [];

    return getFilterableDefinitions(usageDefinitions);
  }, [
    savedViewQuery.data?.payrollSavedCustomFieldView?.fieldDefinitions,
    usageFieldsQuery.data?.payrollFieldsByUsage?.definitions,
  ]);

  const supportedDefinitions = useMemo(
    () => definitions.filter(isSupportedFilterDefinition),
    [definitions],
  );

  const definitionByKey = useMemo(
    () => new Map(supportedDefinitions.map((definition) => [definition.fieldKey, definition])),
    [supportedDefinitions],
  );

  const sectionNames = useMemo(
    () => [...new Set(supportedDefinitions.map(getFieldSection))],
    [supportedDefinitions],
  );

  const queryEmployeeFields = useMutation(api.employeeDataSettings.queries.queryEmployeeFields);

  const persistView = (filters: CustomFieldSelectedFilter[]) => {
    if (!canManageView) return;
    updateSavedView.mutate(
      { input: { viewType, fieldKeys: filters.map((filter) => filter.fieldKey) } },
      { onError: () => {} }, // persistence must never block local filtering
    );
  };

  // restore once, after the saved view and definitions are both available
  useEffect(() => {
    if (hasRestoredSavedView.current || savedViewQuery.isLoading) return;
    const fieldKeys = savedViewQuery.data?.payrollSavedCustomFieldView?.fieldKeys;
    if (supportedDefinitions.length === 0) return;
    hasRestoredSavedView.current = true;
    const restored = getInitialFiltersFromSavedView(fieldKeys, supportedDefinitions);
    if (restored.length > 0) setSelectedFilters(restored);
  }, [savedViewQuery.isLoading, savedViewQuery.data, supportedDefinitions]);

  const applySelectedFilters = async (nextFilters: CustomFieldSelectedFilter[]) => {
    const filtersWithValues = nextFilters.filter((filter) => hasFilterValue(filter.value));

    if (filtersWithValues.length === 0) {
      setErrorMessage(null);
      onClear?.();
      return;
    }

    try {
      setErrorMessage(null);
      const clauses = buildCustomFieldClauses(filtersWithValues, supportedDefinitions);
      const response = await queryEmployeeFields.mutateAsync({
        clauses,
        pageSize: MAX_FILTERED_EMPLOYEES,
      });

      const result = response.payrollQueryEmployeeFields;
      if (result.nextPageToken) {
        throw new Error('Custom field filter returned more than 10,000 employees. Narrow the filter.');
      }

      const userIds = result.userIds ?? [];
      const applyResult: CustomFieldFilterApplyResult = {
        selectedFilters: filtersWithValues,
        clauses,
        userIds,
        peopleIds: extractPeopleIdsFromUserIds(userIds),
        total: result.total ?? userIds.length,
      };

      onApply(applyResult);
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error('Unable to apply filter');
      setErrorMessage(normalizedError.message);
      onError?.(normalizedError);
    }
  };

  const addFilter = (fieldKey: string) => {
    const definition = definitionByKey.get(fieldKey);
    if (!definition) return;

    setSelectedFilters((currentFilters) => {
      if (currentFilters.some((filter) => filter.fieldKey === fieldKey)) {
        return currentFilters;
      }

      const nextFilters = [
        ...currentFilters,
        {
          id: createFilterId(),
          fieldKey,
          value: getInitialFilterValue(definition),
        },
      ];
      persistView(nextFilters);
      return nextFilters;
    });
    setIsAddFilterOpen(false);
  };

  const setFilterValue = (
    fieldKey: string,
    values: CustomFieldSelectedFilter['value'],
    shouldApply = true,
  ) => {
    setSelectedFilters((currentFilters) => {
      const nextFilters = currentFilters.map((filter) =>
        filter.fieldKey === fieldKey ? { ...filter, value: values } : filter,
      );
      if (shouldApply) {
        void applySelectedFilters(nextFilters);
      }
      return nextFilters;
    });
  };

  const clearFilter = (fieldKey: string) => {
    setSelectedFilters((currentFilters) => {
      const nextFilters = currentFilters.filter((filter) => filter.fieldKey !== fieldKey);
      void applySelectedFilters(nextFilters);
      persistView(nextFilters);
      return nextFilters;
    });
    if (openTextFilterKey === fieldKey) {
      setOpenTextFilterKey(null);
    }
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setErrorMessage(null);
    persistView([]);
    onClear?.();
  };

  const applyOpenTextFilter = () => {
    void applySelectedFilters(selectedFilters);
    setOpenTextFilterKey(null);
  };

  const fieldsAvailableToAdd = supportedDefinitions.filter(
    (definition) => !selectedFilters.some((filter) => filter.fieldKey === definition.fieldKey),
  );

  const selectedFiltersWithValues = selectedFilters.filter((filter) => hasFilterValue(filter.value));
  const isLoading = savedViewQuery.isLoading || usageFieldsQuery.isLoading;
  const isDisabled = disabled || isLoading || queryEmployeeFields.isLoading;

  useEffect(() => {
    onSelectedFiltersCountChange?.(selectedFiltersWithValues.length);
  }, [onSelectedFiltersCountChange, selectedFiltersWithValues.length]);

  useEffect(() => {
    onLoadingChange?.(queryEmployeeFields.isLoading);
  }, [onLoadingChange, queryEmployeeFields.isLoading]);

  useEffect(() => {
    return () => onLoadingChange?.(false);
  }, [onLoadingChange]);

  const renderFilterChip = (filter: CustomFieldSelectedFilter) => {
    const definition = definitionByKey.get(filter.fieldKey);
    if (!definition) return null;

    if (isDateDefinition(definition)) {
      return (
        <DateFilterChip
          key={filter.id}
          definition={definition}
          value={filter.value}
          isDisabled={isDisabled}
          onApply={(value) => setFilterValue(definition.fieldKey, value)}
          onClearButtonClick={() => clearFilter(definition.fieldKey)}
        />
      );
    }

    if (!isSelectableDefinition(definition)) {
      return (
        <TextFilterChip
          key={filter.id}
          definition={definition}
          value={typeof filter.value === 'string' ? filter.value : ''}
          isOpen={openTextFilterKey === definition.fieldKey}
          isDisabled={isDisabled}
          isClearDisabled={disabled}
          onOpenChange={(isOpen) => setOpenTextFilterKey(isOpen ? definition.fieldKey : null)}
          onChange={(value) => setFilterValue(definition.fieldKey, value, false)}
          onApply={applyOpenTextFilter}
          onClearButtonClick={() => clearFilter(definition.fieldKey)}
          onClearClick={() => clearFilter(definition.fieldKey)}
        />
      );
    }

    return (
      <EnumFilterChip
        key={filter.id}
        definition={definition}
        value={Array.isArray(filter.value) ? filter.value : []}
        isDisabled={isDisabled}
        onChange={(values) => setFilterValue(definition.fieldKey, values)}
        onClearButtonClick={() => clearFilter(definition.fieldKey)}
      />
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap="spacing.3">
        <Spinner accessibilityLabel="Loading custom field filters" />
        <Text variant="body" size="small">
          Loading custom filters
        </Text>
      </Box>
    );
  }

  // Error card only when the definitions list itself could not be loaded.
  // A failed saved-view lookup alone never reaches this state — definitions
  // fall back to the usage list and the panel renders normally.
  if (supportedDefinitions.length === 0 && usageFieldsQuery.isError) {
    return (
      <Box display="flex" alignItems="center" gap="spacing.4">
        <Text variant="body" size="small" color="feedback.text.negative.intense">
          Couldn’t load filters
        </Text>
        <Button
          variant="secondary"
          size="xsmall"
          isLoading={usageFieldsQuery.isFetching}
          onClick={() => void usageFieldsQuery.refetch()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (supportedDefinitions.length === 0) {
    return (
      <Text variant="body" size="small" color="surface.text.gray.muted">
        No custom payroll filters configured
      </Text>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="spacing.3" width="100%">
      <FilterChipGroup showClearButton={false} onClearButtonClick={clearAllFilters}>
        {selectedFilters.map(renderFilterChip)}

        <AddFilterMenu
          isOpen={isAddFilterOpen}
          isDisabled={isDisabled}
          sectionNames={sectionNames}
          fieldsAvailableToAdd={fieldsAvailableToAdd}
          onOpenChange={setIsAddFilterOpen}
          onAddFilter={addFilter}
        />

        {selectedFiltersWithValues.length > 0 && <Link onClick={clearAllFilters}>Clear Filters</Link>}
      </FilterChipGroup>

      {errorMessage && (
        <Text variant="caption" size="medium" color="feedback.text.negative.intense">
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};
